/**
 * Create Organization Page
 * Form to create a new organization
 */

import { Form, standardSchemaResolver, useForm, z } from "@redenvelopeorg/ofx-electrum"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { apiClient } from "../lib/api"

const createOrgSchema = z.object({
  name: z.string().min(1, { error: "Organization name is required" }),
  displayName: z.string().optional(),
  billingEmail: z.email({ error: "Valid email is required" }),
  entityAssociated: z.string().min(1, { error: "Entity associated is required" }),
  creditLimit: z.string().optional(),
  notes: z.string().optional(),
  // User details
  firstName: z.string().min(2, { error: "First name must be at least 2 characters" }),
  lastName: z.string().min(1, { error: "Last name is required" }),
  email: z.email({ error: "Valid user email is required" }),
  password: z.string().optional().nullable(),
  requestsPerSec: z.string().optional().nullable(),
})

type CreateOrgFormType = z.infer<typeof createOrgSchema>

export function CreateOrganizationPage() {
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()

  const form = useForm<CreateOrgFormType>({
    resolver: standardSchemaResolver(createOrgSchema),
    defaultValues: {
      name: "",
      displayName: "",
      billingEmail: "",
      entityAssociated: "",
      creditLimit: "10000000", // 10 million (more reasonable default)
      notes: "",
      firstName: "",
      lastName: "",
      email: "",
      password: null,
      requestsPerSec: null,
    },
  })

  console.log("Sending organization data:", form.formState.errors)

  const handleSubmit = async (data: CreateOrgFormType) => {
    setIsLoading(true)

    try {
      // Prepare the data to send
      const payload: {
        name: string
        billingEmail: string
        entityAssociated: string
        firstName: string
        lastName: string
        email: string
        displayName?: string
        creditLimit?: number
        notes?: string
        password?: string
        requestsPerSec?: number
      } = {
        name: data.name.trim(),
        billingEmail: data.billingEmail.trim(),
        entityAssociated: data.entityAssociated.trim(),
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
      }

      // Only add optional fields if they have values
      if (data.displayName?.trim()) {
        payload.displayName = data.displayName.trim()
      }

      if (data.creditLimit?.trim()) {
        const creditLimitNum = Number(data.creditLimit)
        if (!Number.isNaN(creditLimitNum) && creditLimitNum >= 0) {
          payload.creditLimit = creditLimitNum
        }
      }

      if (data.notes?.trim()) {
        payload.notes = data.notes.trim()
      }

      if (data.password?.trim()) {
        payload.password = data.password.trim()
        if (payload.password) {
          if (payload.password.length < 3) {
            form.setError("password", {
              type: "manual",
              message: "Password must be at least 3 characters",
            })
          } else {
            form.clearErrors("password")
          }
        }
      }

      if (data.creditLimit?.trim()) {
        const requestPerSecNum = Number(data.requestsPerSec)
        if (!Number.isNaN(requestPerSecNum) && requestPerSecNum >= 0) {
          payload.requestsPerSec = requestPerSecNum
        }
      }

      console.log("Sending organization data:", payload)

      await apiClient.createOrganization(payload)

      // Navigate to organizations list on success
      navigate("/organizations")
    } catch (error) {
      console.error("Failed to create organization:", error)
      const errorMessage = error instanceof Error ? error.message : "Failed to create organization"
      console.error("Error details:", errorMessage)
      form.setError("root", {
        type: "manual",
        message: errorMessage,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "2rem" }}>
      {/* Form Card */}
      <div className="card" style={{ padding: "2.5rem" }}>
        {/* Page Header */}
        <div style={{ marginBottom: "2.5rem", textAlign: "center" }}>
          <div
            style={{
              width: "64px",
              height: "64px",
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              borderRadius: "16px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "1.5rem",
              boxShadow: "0 0 40px rgba(139, 92, 246, 0.4)",
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              <title>Organization icon</title>
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <h1 style={{ fontSize: "2rem", fontWeight: 700, color: "#f1f5f9", marginBottom: "0.5rem" }}>
            Create New Organization
          </h1>
          <p style={{ color: "#94a3b8", fontSize: "1rem" }}>Fill in the details below to create a new organization</p>
        </div>

        <Form form={form} schema={createOrgSchema} onSubmit={handleSubmit}>
          <div className="flex flex-col gap-6">
            {/* Basic Information */}
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  marginBottom: "1.5rem",
                  paddingBottom: "0.75rem",
                  borderBottom: "2px solid rgba(139, 92, 246, 0.3)",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    background: "linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(139, 92, 246, 0.3)",
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2">
                    <title>Info icon</title>
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4M12 8h.01" />
                  </svg>
                </div>
                <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#e2e8f0" }}>Basic Information</h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
                <div>
                  <label
                    htmlFor="name"
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#e2e8f0",
                    }}
                  >
                    Organization Name *
                  </label>
                  <input {...form.register("name")} id="name" type="text" placeholder="Acme Corporation" />
                  {form.formState.errors.name && (
                    <p style={{ color: "#fca5a5", fontSize: "0.8125rem", marginTop: "0.5rem" }}>
                      {form.formState.errors.name.message}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="displayName"
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#e2e8f0",
                    }}
                  >
                    Display Name
                  </label>
                  <input {...form.register("displayName")} id="displayName" type="text" placeholder="ACME Corp" />
                </div>
              </div>
            </div>

            {/* Contact & Entity */}
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  marginBottom: "1.5rem",
                  paddingBottom: "0.75rem",
                  borderBottom: "2px solid rgba(139, 92, 246, 0.3)",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    background: "linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(139, 92, 246, 0.3)",
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2">
                    <title>Email icon</title>
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#e2e8f0" }}>Contact & Entity Details</h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
                <div>
                  <label
                    htmlFor="billingEmail"
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#e2e8f0",
                    }}
                  >
                    Billing Email *
                  </label>
                  <input
                    {...form.register("billingEmail")}
                    id="billingEmail"
                    type="email"
                    placeholder="billing@acme.com"
                  />
                  {form.formState.errors.billingEmail && (
                    <p style={{ color: "#fca5a5", fontSize: "0.8125rem", marginTop: "0.5rem" }}>
                      {form.formState.errors.billingEmail.message}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="entityAssociated"
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#e2e8f0",
                    }}
                  >
                    Entity Associated *
                  </label>
                  <select {...form.register("entityAssociated")} id="entityAssociated">
                    <option value="">Select an entity</option>
                    <option value="ALPHA">ALPHA</option>
                    <option value="INFINITY_ALPHA">INFINITY_ALPHA</option>
                    <option value="INFINITY_BRAVO">INFINITY_BRAVO</option>
                  </select>
                  {form.formState.errors.entityAssociated && (
                    <p style={{ color: "#fca5a5", fontSize: "0.8125rem", marginTop: "0.5rem" }}>
                      {form.formState.errors.entityAssociated.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* User Details */}
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  marginBottom: "1.5rem",
                  paddingBottom: "0.75rem",
                  borderBottom: "2px solid rgba(139, 92, 246, 0.3)",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    background: "linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(139, 92, 246, 0.3)",
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2">
                    <title>User icon</title>
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#e2e8f0" }}>Admin User Details</h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem", marginBottom: "1.5rem" }}>
                <div>
                  <label
                    htmlFor="firstName"
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#e2e8f0",
                    }}
                  >
                    First Name *
                  </label>
                  <input {...form.register("firstName")} id="firstName" type="text" placeholder="John" />
                  {form.formState.errors.firstName && (
                    <p style={{ color: "#fca5a5", fontSize: "0.8125rem", marginTop: "0.5rem" }}>
                      {form.formState.errors.firstName.message}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="lastName"
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#e2e8f0",
                    }}
                  >
                    Last Name *
                  </label>
                  <input {...form.register("lastName")} id="lastName" type="text" placeholder="Doe" />
                  {form.formState.errors.lastName && (
                    <p style={{ color: "#fca5a5", fontSize: "0.8125rem", marginTop: "0.5rem" }}>
                      {form.formState.errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
                <div>
                  <label
                    htmlFor="email"
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#e2e8f0",
                    }}
                  >
                    Email *
                  </label>
                  <input {...form.register("email")} id="email" type="email" placeholder="john.doe@acme.com" />
                  {form.formState.errors.email && (
                    <p style={{ color: "#fca5a5", fontSize: "0.8125rem", marginTop: "0.5rem" }}>
                      {form.formState.errors.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="password"
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#e2e8f0",
                    }}
                  >
                    Password (optional, defaults to "ofx123")
                  </label>
                  <input {...form.register("password")} id="password" type="password" placeholder="••••••••" />
                  {form.formState.errors.password && (
                    <p style={{ color: "#fca5a5", fontSize: "0.8125rem", marginTop: "0.5rem" }}>
                      {form.formState.errors.password.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Account Configuration */}
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  marginBottom: "1.5rem",
                  paddingBottom: "0.75rem",
                  borderBottom: "2px solid rgba(139, 92, 246, 0.3)",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    background: "linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(139, 92, 246, 0.3)",
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2">
                    <title>Settings icon</title>
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 1v6m0 6v6M4.93 4.93l4.24 4.24m5.66 5.66l4.24 4.24M1 12h6m6 0h6M4.93 19.07l4.24-4.24m5.66-5.66l4.24-4.24" />
                  </svg>
                </div>
                <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#e2e8f0" }}>Account Configuration</h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
                <div>
                  <label
                    htmlFor="creditLimit"
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#e2e8f0",
                    }}
                  >
                    Credit Limit
                  </label>
                  <input {...form.register("creditLimit")} id="creditLimit" type="text" placeholder="10000000" />
                </div>

                <div>
                  <label
                    htmlFor="requestsPerSec"
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "#e2e8f0",
                    }}
                  >
                    Allowed Req / Sec (optional, defaults to "10")
                  </label>
                  <input {...form.register("requestsPerSec")} id="requestsPerSec" type="text" placeholder="10" />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  marginBottom: "1.5rem",
                  paddingBottom: "0.75rem",
                  borderBottom: "2px solid rgba(139, 92, 246, 0.3)",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    background: "linear-gradient(135deg, rgba(99, 102, 241, 0.2), rgba(139, 92, 246, 0.2))",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(139, 92, 246, 0.3)",
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="2">
                    <title>Notes icon</title>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                </div>
                <h2 style={{ fontSize: "1.25rem", fontWeight: 600, color: "#e2e8f0" }}>Additional Notes</h2>
              </div>
              <div>
                <label
                  htmlFor="notes"
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "#e2e8f0",
                  }}
                >
                  Notes
                </label>
                <textarea
                  {...form.register("notes")}
                  id="notes"
                  placeholder="Additional notes about this organization..."
                  rows={4}
                  style={{
                    width: "100%",
                    resize: "vertical",
                  }}
                />
              </div>
            </div>

            {/* Error Display */}
            {form.formState.errors.root && (
              <div
                style={{
                  padding: "1rem 1.25rem",
                  background: "rgba(239, 68, 68, 0.1)",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                  color: "#fca5a5",
                  borderRadius: "12px",
                  fontSize: "0.875rem",
                  display: "flex",
                  alignItems: "start",
                  gap: "0.75rem",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{ flexShrink: 0, marginTop: "0.125rem" }}
                >
                  <title>Alert icon</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{form.formState.errors.root.message}</span>
              </div>
            )}

            {/* Actions */}
            <div
              style={{
                display: "flex",
                gap: "1rem",
                paddingTop: "2rem",
                borderTop: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: "1rem 2rem",
                  background: isLoading
                    ? "rgba(100, 116, 139, 0.5)"
                    : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)",
                  border: "none",
                  borderRadius: "12px",
                  color: "white",
                  fontSize: "1rem",
                  fontWeight: 600,
                  cursor: isLoading ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: isLoading ? "none" : "0 0 30px rgba(139, 92, 246, 0.3), 0 10px 25px rgba(0, 0, 0, 0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.transform = "translateY(-2px)"
                    e.currentTarget.style.boxShadow = "0 0 40px rgba(139, 92, 246, 0.4), 0 15px 35px rgba(0, 0, 0, 0.4)"
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)"
                  e.currentTarget.style.boxShadow = "0 0 30px rgba(139, 92, 246, 0.3), 0 10px 25px rgba(0, 0, 0, 0.3)"
                }}
              >
                {isLoading ? (
                  <>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      className="animate-spin"
                    >
                      <title>Loading spinner</title>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Creating...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <title>Plus icon</title>
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    Create Organization
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate("/organizations")}
                disabled={isLoading}
                style={{
                  padding: "1rem 2rem",
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "12px",
                  color: "#e2e8f0",
                  fontSize: "1rem",
                  fontWeight: 600,
                  cursor: isLoading ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)"
                    e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.15)"
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.1)"
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  )
}
